import { createContext, useContext, useEffect, useState } from "react";

// Static exchange rates from USD (approximate, Feb 2026). Update periodically.
export const CURRENCIES = [
  { code: "USD", symbol: "$",     rate: 1.0,    decimals: 0, name: "US Dollar",          flag: "🇺🇸" },
  { code: "MXN", symbol: "MX$",   rate: 20.0,   decimals: 0, name: "Peso Mexicano",      flag: "🇲🇽" },
  { code: "BRL", symbol: "R$",    rate: 5.8,    decimals: 0, name: "Real Brasileiro",    flag: "🇧🇷" },
  { code: "EUR", symbol: "€",     rate: 0.92,   decimals: 0, name: "Euro",               flag: "🇪🇺" },
  { code: "GBP", symbol: "£",     rate: 0.79,   decimals: 0, name: "British Pound",      flag: "🇬🇧" },
  { code: "COP", symbol: "COL$",  rate: 4100,   decimals: 0, name: "Peso Colombiano",    flag: "🇨🇴" },
  { code: "CLP", symbol: "CL$",   rate: 970,    decimals: 0, name: "Peso Chileno",       flag: "🇨🇱" },
  { code: "ARS", symbol: "AR$",   rate: 860,    decimals: 0, name: "Peso Argentino",     flag: "🇦🇷" },
  { code: "PEN", symbol: "S/.",   rate: 3.75,   decimals: 0, name: "Sol Peruano",        flag: "🇵🇪" },
  { code: "CAD", symbol: "CA$",   rate: 1.36,   decimals: 0, name: "Canadian Dollar",    flag: "🇨🇦" },
  { code: "AUD", symbol: "AU$",   rate: 1.52,   decimals: 0, name: "Australian Dollar",  flag: "🇦🇺" },
  { code: "JPY", symbol: "¥",     rate: 150,    decimals: 0, name: "Japanese Yen",       flag: "🇯🇵" },
  { code: "CHF", symbol: "CHF",   rate: 0.88,   decimals: 0, name: "Swiss Franc",        flag: "🇨🇭" },
];

const COUNTRY_TO_CURRENCY = {
  MX: "MXN", BR: "BRL", CO: "COP", CL: "CLP", AR: "ARS", PE: "PEN",
  GB: "GBP", CA: "CAD", AU: "AUD", JP: "JPY", CH: "CHF",
  // Eurozone
  DE: "EUR", FR: "EUR", IT: "EUR", ES: "EUR", NL: "EUR", BE: "EUR", IE: "EUR",
  PT: "EUR", AT: "EUR", FI: "EUR", GR: "EUR", LU: "EUR", CY: "EUR", MT: "EUR",
  SI: "EUR", SK: "EUR", EE: "EUR", LV: "EUR", LT: "EUR", HR: "EUR",
  // Default
  US: "USD",
};

const COUNTRY_TO_LANG = {
  MX: "es", ES: "es", AR: "es", CL: "es", CO: "es", PE: "es", UY: "es", VE: "es", EC: "es", BO: "es", PY: "es", CR: "es", PA: "es", DO: "es", GT: "es", HN: "es", NI: "es", SV: "es", PR: "es",
  BR: "pt", PT: "pt",
  FR: "fr", BE: "fr", LU: "fr", CH: "fr", CA: "fr", MC: "fr",
  DE: "de", AT: "de",
  IT: "it", SM: "it", VA: "it",
  US: "en", GB: "en", CA2: "en", AU: "en", NZ: "en", IE: "en", IN: "en", PH: "en", ZA: "en",
};

const CurrencyContext = createContext(null);

export function CurrencyProvider({ children }) {
  const [currency, setCurrency] = useState(() => localStorage.getItem("cia_currency") || "USD");
  const [autoDetected, setAutoDetected] = useState(false);

  useEffect(() => {
    localStorage.setItem("cia_currency", currency);
  }, [currency]);

  // Auto-detect on first visit (no localStorage for currency AND no localStorage for lang)
  useEffect(() => {
    const storedCur = localStorage.getItem("cia_currency");
    const storedLang = localStorage.getItem("cia_lang");
    if (storedCur && storedLang) return; // user already has preferences

    let cancelled = false;
    (async () => {
      try {
        const r = await fetch("https://ipapi.co/json/", { cache: "force-cache" });
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled) return;
        const country = (data.country_code || "").toUpperCase();
        const cur = COUNTRY_TO_CURRENCY[country] || data.currency || "USD";
        const lang = COUNTRY_TO_LANG[country] || "en";
        if (!storedCur && CURRENCIES.find((c) => c.code === cur)) {
          setCurrency(cur);
        }
        if (!storedLang && ["es", "en", "pt", "fr", "de", "it"].includes(lang)) {
          const i18nMod = await import("@/i18n");
          i18nMod.default.changeLanguage(lang);
        }
        setAutoDetected(true);
      } catch {
        /* offline / blocked — stick with defaults */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const info = CURRENCIES.find((c) => c.code === currency) || CURRENCIES[0];

  const convert = (usdAmount) => usdAmount * info.rate;

  const format = (usdAmount) => {
    const v = convert(usdAmount);
    const rounded = info.decimals === 0
      ? Math.round(v).toLocaleString("en-US", { maximumFractionDigits: 0 })
      : v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${info.symbol}${rounded}`;
  };

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, info, convert, format, autoDetected }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used inside CurrencyProvider");
  return ctx;
}
